/**
 * Centralized TokenManager for the Novata Excel Plugin.
 *
 * Encapsulates all token persistence, retrieval, expiry-checking and
 * access-token extraction logic in one place.  Accepts a `storage` object
 * via constructor injection so it can be tested without touching real
 * browser APIs.
 *
 * Storage interface:
 *   { getSensitiveItem(key), setSensitiveItem(key, value), removeSensitiveItem(key), clearAllSensitiveData() }
 */
import logger from "../../logger";

const TOKEN_KEYS = ["authTokens", "oktaTokens"];
const USER_INFO_KEY = "userInfo";
const DEFAULT_TOKEN_TTL = 3600; // seconds
const USER_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in ms

export class TokenManager {
  /**
   * @param {object} storage – object implementing getSensitiveItem / setSensitiveItem /
   *   removeSensitiveItem / clearAllSensitiveData.  Defaults to the real storageService.
   */
  constructor(storage) {
    this._storage = storage;
  }

  // ── Token persistence ────────────────────────────────────────────────────

  /**
   * Persist tokens to secure storage.
   * Normalises the expiry before saving.
   */
  async persistTokens(tokens) {
    const data = {
      accessToken: tokens.accessToken,
      idToken: tokens.idToken,
      refreshToken: tokens.refreshToken,
      expiresAt:
        tokens.expiresAt ||
        tokens.accessToken?.expiresAt ||
        tokens.idToken?.expiresAt ||
        Math.floor(Date.now() / 1000) + DEFAULT_TOKEN_TTL,
    };

    await this._storage.setSensitiveItem("authTokens", JSON.stringify(data));
    logger.debug("Tokens persisted");
  }

  // ── Token retrieval ──────────────────────────────────────────────────────

  /**
   * Restore tokens from storage.
   * Checks both `authTokens` and legacy `oktaTokens` keys.
   * Returns the parsed tokens if still valid, otherwise clears them and
   * returns null.
   */
  async restoreTokens() {
    try {
      const tokensJson = await this._getFirstAvailable(TOKEN_KEYS);
      if (!tokensJson) return null;

      const tokens = JSON.parse(tokensJson);
      const expiresAt = this._resolveExpiry(tokens);

      if (expiresAt && expiresAt > Math.floor(Date.now() / 1000)) {
        logger.debug("Valid tokens restored from storage");
        return tokens;
      }

      logger.warn("Tokens expired, clearing storage");
      await this._removeAll(TOKEN_KEYS);
      return null;
    } catch (err) {
      logger.error("Error restoring tokens: %s", err?.message);
      return null;
    }
  }

  /**
   * Check whether valid (non-expired) tokens exist in storage.
   * Returns `{ authenticated: true }` or `{ authenticated: false, error }`.
   */
  async checkAuthentication() {
    try {
      const tokensJson = await this._getFirstAvailable(TOKEN_KEYS);

      if (!tokensJson) {
        return {
          authenticated: false,
          error: "Please login first. Open the Novata task pane to authenticate.",
        };
      }

      const tokens = JSON.parse(tokensJson);
      const expiresAt = this._resolveExpiry(tokens);

      if (expiresAt && expiresAt > Math.floor(Date.now() / 1000)) {
        return { authenticated: true };
      }

      return {
        authenticated: false,
        error: "Session expired. Please login again through the Novata task pane.",
      };
    } catch (error) {
      logger.error("Authentication check error: %s", error?.message);
      return {
        authenticated: false,
        error: "Authentication check failed. Please try logging in again.",
      };
    }
  }

  /**
   * Extract the raw access-token string from stored tokens.
   * Handles the multiple shapes the token object may take.
   */
  async getAccessToken() {
    try {
      const tokensJson = await this._getFirstAvailable(TOKEN_KEYS);
      if (!tokensJson) return null;

      const tokens = JSON.parse(tokensJson);
      return this._extractAccessToken(tokens);
    } catch (error) {
      logger.error("Error getting access token: %s", error?.message);
      return null;
    }
  }

  /**
   * Clear all token-related data from storage.
   */
  async clearTokens() {
    await this._storage.clearAllSensitiveData();
  }

  // ── User info ────────────────────────────────────────────────────────────

  /**
   * Persist user profile information to secure storage.
   */
  async persistUserInfo(user) {
    if (!user) {
      logger.warn("No user info to persist");
      return;
    }

    const userData = {
      email: user.email,
      name: user.name,
      sub: user.sub,
      preferred_username: user.preferred_username,
      ...user,
      cachedAt: Date.now(),
    };

    await this._storage.setSensitiveItem(USER_INFO_KEY, JSON.stringify(userData));
    logger.debug("User info saved");
  }

  /**
   * Restore cached user info if it hasn't expired (24h TTL).
   */
  async restoreUserInfo() {
    try {
      const saved = await this._storage.getSensitiveItem(USER_INFO_KEY);
      if (!saved) return null;

      const userData = JSON.parse(saved);

      if (userData.cachedAt && Date.now() - userData.cachedAt > USER_CACHE_EXPIRY) {
        logger.warn("Cached user info expired, clearing...");
        await this._storage.removeSensitiveItem(USER_INFO_KEY);
        return null;
      }

      logger.debug("User info restored from cache");
      return userData;
    } catch (err) {
      logger.error("Error restoring user info: %s", err?.message);
      return null;
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  /**
   * Return the first non-null value found across the given storage keys.
   */
  async _getFirstAvailable(keys) {
    for (const key of keys) {
      const value = await this._storage.getSensitiveItem(key);
      if (value) return value;
    }
    return null;
  }

  /**
   * Remove all given keys from storage.
   */
  async _removeAll(keys) {
    for (const key of keys) {
      await this._storage.removeSensitiveItem(key);
    }
  }

  /**
   * Resolve the expiry timestamp from the different shapes it may appear in.
   */
  _resolveExpiry(tokens) {
    if (tokens.expiresAt) return tokens.expiresAt;
    if (tokens.accessToken?.expiresAt) return tokens.accessToken.expiresAt;
    if (tokens.idToken?.expiresAt) return tokens.idToken.expiresAt;
    return null;
  }

  /**
   * Extract the raw access token string from the various shapes
   * it may take in storage.
   */
  _extractAccessToken(tokens) {
    if (!tokens.accessToken) return null;
    if (typeof tokens.accessToken === "string") return tokens.accessToken;
    if (tokens.accessToken.value) return tokens.accessToken.value;
    if (tokens.accessToken.accessToken) return tokens.accessToken.accessToken;
    return null;
  }
}

// ── Default singleton ────────────────────────────────────────────────────────

import {
  getSensitiveItem,
  setSensitiveItem,
  removeSensitiveItem,
  clearAllSensitiveData,
} from "./storageService";

const defaultStorage = {
  getSensitiveItem,
  setSensitiveItem,
  removeSensitiveItem,
  clearAllSensitiveData,
};

const tokenManager = new TokenManager(defaultStorage);
export default tokenManager;
